import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import {
  CheckCircleOutline as SentIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const formatSentAt = (value) => {
  if (!value) return 'Date unavailable';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

const RecipientLine = ({ label, values = [] }) => (
  <Stack direction="row" spacing={0.75} alignItems="flex-start">
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 28, pt: 0.3 }}>
      {label}
    </Typography>
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
      {values.length
        ? values.map((value) => <Chip key={value} size="small" label={value} variant="outlined" />)
        : <Typography variant="caption" color="text.secondary">None</Typography>}
    </Stack>
  </Stack>
);

export default function EmailDeliveryHistory({
  items = [],
  loading = false,
  error = '',
  onRefresh
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, 0.85),
        bgcolor: alpha(theme.palette.background.paper, 0.48)
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.25 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Sent History</Typography>
          <Typography variant="caption" color="text.secondary">
            Delivery recipients and sending mailbox.
          </Typography>
        </Box>
        {onRefresh && (
          <Button
            size="small"
            variant="text"
            startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={onRefresh}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
        )}
      </Stack>

      {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}
      {loading && !items.length && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading sent emails...</Typography>
        </Stack>
      )}
      {!loading && !items.length && !error && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No emails have been recorded for this record yet.
        </Typography>
      )}

      <Stack spacing={1.25} divider={<Divider flexItem />}>
        {items.map((item) => (
          <Box key={item.id}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.6} alignItems="center">
                  <SentIcon color="success" sx={{ fontSize: 17 }} />
                  <Typography variant="body2" sx={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.subject || 'No subject'}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {formatSentAt(item.sent_at)} by {item.sent_by_name || item.sent_by_email || 'System user'}
                </Typography>
              </Box>
              <Chip size="small" color="success" label={item.delivery_status || 'sent'} />
            </Stack>

            <Stack spacing={0.75} sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                <strong>From:</strong> {item.from_address || 'Not recorded'}
              </Typography>
              <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                <strong>Reply-To:</strong> {item.reply_to_address || 'Same as From'}
              </Typography>
              <RecipientLine label="To" values={item.to} />
              <RecipientLine label="CC" values={item.cc} />
              <Typography variant="caption" color="text.secondary">
                {Number(item.attachment_count) || 0} attachment(s)
              </Typography>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
